/**
 * AgentBrief
 * 
 * @Author: Focci
 * @Date: 2023-08-31 09:29:27
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-31 09:29:27
 */

'use client'

import PropTypes from 'prop-types'
import AspectRatioCover from '@comp/AspectRatioCover'
import SVGEmail from '@img/email.svg'
import PhoneButton from '@comp/page/property/agent-card/PhoneButton'
import PremiumEnquiry from '@comp/page/common/card/agent/premium/PremiumEnquiry'
import useTracking from '@hook/useTracking'
import Link from 'next/link'
import { officeDetail } from '@lib/route/agent'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useRef } from 'react'
import { ButtonBase } from '@mui/material'
import AgentAvatar from '../AgentAvatar'

export function PremiumAgentPhoneButton({ agent, ...props }) {
  return (
    <PhoneButton
      agentId={agent.id}
      phone={agent.phone_number}
      trackParams={{ agencyOfficeId: agent.office_id }}
      className="
        flex items-center justify-center gap-1 w-full hover:bg-cf8 cursor-pointer
        h-8 border border-ce6 hover:border-helper rounded-sm
      "
      {...props}
    />
  )
}

export function PremiumAgentEmailButton({ agent, ...props }) {
  const enquiryDialog = useRef()
  const ts = useTranslations('search')
  const { pushTrack } = useTracking()

  const handleEmailClick = useCallback(() => {
    enquiryDialog.current?.openDialog?.()
    pushTrack('click_email', {
      houseId: 0,
      agentId: agent?.id,
      agencyOfficeId: agent.office_id
    })
  }, [pushTrack, agent])

  return (
    <>
      <ButtonBase className="rounded-sm" onClick={handleEmailClick}>
        <div
          className="
            flex items-center justify-center gap-1 w-full bg-primary hover:bg-primary/90
            h-8 text-white rounded-sm
          "
          {...props}
        >
          <SVGEmail />
          {ts('emailInquiry')}
        </div>
      </ButtonBase>
      <PremiumEnquiry ref={enquiryDialog} agentId={agent.id} />
    </>
  )
}

export default function AgentBrief({
  data = {}
}) {
  const locale = useLocale()
  const url = officeDetail(data?.office_slug, locale)
  
  return (
    <div className="w-full md:w-[16rem] max-w-full bg-white rounded-sm mt-4">
      <div
        className="h-10 rounded-t-sm flex justify-center items-center"
        style={{ backgroundColor: data.underline_color }}
      >
        <Link href={url} className="w-20 inline-block">
          <AspectRatioCover
            brand=""
            className="object-contain" 
            src={data.office_oss_avatar}
            width={100}
            height={40}
            alt={data.office_name}
          />
        </Link>
      </div>
      <div className="bg-white p-4 rounded-b-sm border flex flex-col items-center">
        <AgentAvatar
          name={data.name}
          avatar={data.oss_avatar}
          membershipLevel={data.membership_level}
          width={120}
          height={120}
          className="object-cover rounded-full object-top bg-cf0"
        />
        <h1 className="mt-2 mb-1 text-f1.2 font-bold">
          {data.name}
        </h1>
        <div className="flex flex-col items-center justify-center text-f.8 gap-y-1 mt-1">
          {data.job_title && <span className="text-center">{data.job_title}</span>}
          {data.language && <span>{data.language}</span>}
        </div>
        <div className="flex flex-col gap-2 mt-4 w-full">
          <PremiumAgentPhoneButton agent={data} />
          <PremiumAgentEmailButton agent={data} />
        </div>
        {data.office_name && (
          <Link href={url} className="text-center mt-4 pt-4 border-t border-ce6 hover:underline">
            {data.office_name}
          </Link>
        )}
      </div>
    </div>
  )
}

AgentBrief.propTypes = {
  data: PropTypes.object,
}
